import React from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent } from './ui/card';
import { ExternalLink, FileText, File } from 'lucide-react';
import { Button } from './ui/button';

const ResourceCard = ({ resource }) => {
  const typeLabels = {
    'blog': 'Blog',
    'case-study': 'Case Study',
    'rnd': 'R&D',
    'soft-paper': 'Soft Paper',
    'pdf': 'PDF'
  };

  const typeColors = {
    'blog': 'bg-blue-100 text-blue-700 border-blue-300',
    'case-study': 'bg-purple-100 text-purple-700 border-purple-300',
    'rnd': 'bg-green-100 text-green-700 border-green-300',
    'soft-paper': 'bg-orange-100 text-orange-700 border-orange-300',
    'pdf': 'bg-red-100 text-red-700 border-red-300'
  };

  const handleCardClick = () => {
    if (resource.source === 'external-url' && resource.externalUrl) {
      window.open(resource.externalUrl, '_blank', 'noopener,noreferrer');
    } else if (resource.source === 'pdf' && resource.pdfUrl) {
      window.open(resource.pdfUrl, '_blank', 'noopener,noreferrer');
    }
  };

  const renderContent = () => (
    <Card className="bg-white border-gray-200 hover:border-blue-400 hover:shadow-xl transition-all duration-300 hover:scale-[1.02] group h-full">
      <CardContent className="p-0">
        {/* Cover Image */}
        <div className="relative h-48 bg-gradient-to-br from-blue-100 to-sky-100 overflow-hidden">
          {resource.coverImageUrl ? (
            <img 
              src={resource.coverImageUrl} 
              alt={resource.title}
              className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center">
              <FileText className="w-16 h-16 text-gray-300" />
            </div>
          )}
          
          {/* Type Badge */}
          <div className="absolute top-3 left-3">
            <span className={`px-3 py-1 rounded-full text-xs font-medium border backdrop-blur-sm ${typeColors[resource.type]}`}>
              {typeLabels[resource.type]}
            </span>
          </div>

          {/* Source Indicator */}
          {resource.source === 'external-url' && (
            <div className="absolute top-3 right-3 bg-white/90 rounded-full p-2">
              <ExternalLink className="w-4 h-4 text-blue-600" />
            </div>
          )}
          {resource.source === 'pdf' && (
            <div className="absolute top-3 right-3 bg-white/90 rounded-full p-2">
              <File className="w-4 h-4 text-blue-600" />
            </div>
          )}
        </div>

        {/* Content */}
        <div className="p-6">
          <h3 className="text-xl font-semibold text-gray-900 mb-2 line-clamp-2 group-hover:text-blue-600 transition-colors">
            {resource.title}
          </h3>
          
          <p className="text-gray-600 text-sm mb-4 line-clamp-3">
            {resource.shortDescription}
          </p>

          {/* Tags */}
          {resource.tags && resource.tags.length > 0 && (
            <div className="flex flex-wrap gap-2 mb-4">
              {resource.tags.slice(0, 3).map((tag, index) => (
                <span 
                  key={index}
                  className="px-2 py-1 rounded-md text-xs bg-gray-100 text-gray-700 border border-gray-200"
                >
                  {tag}
                </span>
              ))}
            </div>
          )}

          {/* Source Label */}
          <div className="text-xs text-gray-500 font-medium">
            {resource.source === 'external-url' && 'External Link'}
            {resource.source === 'pdf' && 'PDF Document'}
            {resource.source === 'internal' && 'Read More'}
          </div>
        </div>
      </CardContent>
    </Card>
  );

  // For internal content, use Link; for external/PDF, use div with onClick
  if (resource.source === 'internal') {
    return (
      <Link to={`/resources/${resource.slug}`} className="block h-full">
        {renderContent()}
      </Link>
    );
  }

  return (
    <div onClick={handleCardClick} className="cursor-pointer h-full">
      {renderContent()}
    </div>
  );
};

export default ResourceCard;
